import { useState } from "react"
import { useAppNotificationStore } from "stores"
import {
  DEFAULT_ALLOWED_EXTENSIONS,
  isAllowedFile,
  isWithinSizeLimit,
  sendToWorker,
} from "utils/file-upload"
import type { ParseRequest, ParseResponse } from "utils/file-upload"

/**
 * Generic CSV / XLSX file-upload hook.
 *
 * What it does (in order):
 *  1. Validates the file's extension against `allowedExtensions`.
 *  2. Validates the file's size against `maxFileSizeMB`.
 *  3. Reads the file into an ArrayBuffer.
 *  4. Spawns the parse worker and waits for `{ headers, rows }`.
 *  5. Calls `onParse(headers, rows)` so the caller can map / preview / submit.
 *
 * What it does NOT do:
 *  - Map/transform rows into a domain-specific shape — that's the caller's
 *    job (often done in another worker; see useTimesheetRowMapper for an
 *    example).
 *  - POST anything to a backend.
 *  - Render any UI.
 *
 * SOLID notes:
 *  - SRP: orchestrates validation + parse; nothing else.
 *  - OCP: extension list, size limit, and result handlers are injected;
 *    no need to fork the hook to support new file types or pipelines.
 *  - DIP: depends on the abstract `isAllowedFile` / `sendToWorker`
 *    primitives, not on a specific worker path or extension list.
 *
 * Usage:
 *   const { isProcessing, processFile } = useFileUpload({
 *     onFileChange: setFile,
 *     onParse: (headers, rows) => { ... },
 *     allowedExtensions: ["csv", "xlsx"],
 *     maxFileSizeMB: 50,
 *     acceptedFormatsLabel: "CSV or XLSX",
 *   })
 *
 *   <DropZone onFileSelected={processFile} isProcessing={isProcessing} ... />
 */

export interface UseFileUploadParams {
  /** Called with the File on success, or `null` if parsing fails. */
  onFileChange: (file: File | null) => void
  /** Called with raw parsed rows once the worker finishes. */
  onParse: (headers: string[], rows: Record<string, unknown>[]) => void
  /** Allow-list of file extensions (lower-case, no dot). */
  allowedExtensions?: string[]
  /** Maximum file size in megabytes. */
  maxFileSizeMB?: number
  /** Human-readable label used in the "unsupported type" toast (e.g. "CSV or XLSX"). */
  acceptedFormatsLabel?: string
}

export interface UseFileUploadResult {
  /** True while the worker is parsing — wire to button disabled / spinner. */
  isProcessing: boolean
  /** Hand a File from a drop event or <input>; the hook does the rest. */
  processFile: (file: File) => Promise<void>
}

const spawnParseWorker = () =>
  new Worker(
    new URL("../utils/file-upload/xlsx-parse.worker.ts", import.meta.url),
    { type: "module" }
  )

const parseInWorker = (buffer: ArrayBuffer) =>
  sendToWorker<
    ParseResponse,
    Extract<ParseResponse, { type: "parse-success" }>
  >(
    spawnParseWorker,
    { type: "parse", buffer } satisfies ParseRequest,
    "parse-success",
    [buffer]
  )

export const useFileUpload = ({
  onFileChange,
  onParse,
  allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
  maxFileSizeMB = 50,
  acceptedFormatsLabel = "CSV or XLSX",
}: UseFileUploadParams): UseFileUploadResult => {
  const { toast } = useAppNotificationStore()
  const [isProcessing, setIsProcessing] = useState(false)

  const processFile = async (file: File) => {
    if (!isAllowedFile(file, allowedExtensions)) {
      toast.error(`Unsupported file type. Please upload a ${acceptedFormatsLabel} file.`)
      return
    }

    if (!isWithinSizeLimit(file, maxFileSizeMB)) {
      toast.error(`File is too large. Maximum size is ${maxFileSizeMB}MB.`)
      return
    }

    onFileChange(file)
    setIsProcessing(true)

    try {
      const buffer = await file.arrayBuffer()
      const { headers, rows } = await parseInWorker(buffer)

      if (!rows.length) {
        toast.error("This sheet has no data rows.")
        onFileChange(null)
        return
      }

      onParse(headers, rows)
    } catch (error) {
      console.error("File parsing failed:", error)
      toast.error(
        "Could not read this file. Make sure it's a valid CSV or Excel sheet."
      )
      onFileChange(null)
    } finally {
      setIsProcessing(false)
    }
  }

  return { isProcessing, processFile }
}
